import { useCallback, useEffect, useMemo, useState } from "react";
import QuestionHeader from "../QuestionHeader";
import { LeaseApplicationService, LeaseApplicationTemplateSectionVM, QuestionTypeEnum, TemplateEntityTypesEnum, TemplateService, TemplateTypeEnum } from "@propertelligent/client-api";
import { useNavigate, useParams, useLocation } from "react-router";
import "./AddTemplate.css"
import RequestBreadcrumb from "../../../common/RequestBreadcrumb";
import AddTraspButton from "../../../firstTimeUserExperience/commonCom/AddTraspButton";
import AddButtonWithArrow from "../../../firstTimeUserExperience/commonCom/AddButtonWithArrow";
import QuestionOptional from "../QuestionOptional";
import EmploymentHistoryEdit from "../EmploymentHistoryEdit";
import Reference from "../RefrenceEdit";
import QuestionHistoryEdit from "../QuestionEditTemplete";
import FeeTemplete from "../FeeTemplete";
import ConfirmSignature from "../ConfirmSignature";
import BoardingReview from "../../../TenantOnboard/RenderSteps/BoardingReview";
import apiHelper from "../../../../utils/apiHelper";
import { UserLoginData } from "../../../common/charts/Enums";
import { GetImages } from "../../../../utils/GetImages";
import SectionHeader from "../../../TenantOnboard/Header/SectionHeader";
import CustomTooltip from "../../../common/CustomTooltip";
import { OverlayTrigger } from "react-bootstrap";
import { usePermissions } from "../../../../hooks/usePermissions";
import { PermissionGuard } from "../../../../newComponents/PermissionGuard";
import NoAccess from "../../../../pages/NoAccess";
import { AccessLevelEnum } from "@propertelligent/client-api";

const AddTemplate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [activeSection, setActiveSection] = useState("");
  const [previewApplication, setPreviewApplication] = useState(false)
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [postData, setPostData] = useState<any | undefined>(
    {
      name: "New Tenant Application",
      description: "Application for prospective tenants",
      templateTypeId: TemplateTypeEnum.LeaseApplication,
      entityTypeId: TemplateEntityTypesEnum.Organization,
      entityId: parseInt(sessionStorage.getItem(UserLoginData.organization_id)),
      isInUse: false,
      leaseApplicationTemplateSections: {
        CreditCheck: { requiredRange: 2 },
        CurrentAddress: { requiredRange: 2 },
        RentIncomeCheck: { requiredRange: 2 },
        EmploymentHistory: { requiredRange: 2 },
        References: { requiredRange: 2 },
        Questionnaires: {
          questions: [{
            id: 1, questionTypeId: QuestionTypeEnum.SingleChoice,
            questionText: "Do you smoke?", isRequired: false,
            choices: [{ id: 1, optionText: "Yes" }, { id: 2, optionText: "No" }],
            followUpQuestionChoiceId: 1, followUpQuestionText: "Please provide details?"
          }]
        },
        Signature: { clause: "Are you agree for everything" },
        ApplicationFees: { clause: " Fee is non refundable", fee: 50 }
      } as LeaseApplicationTemplateSectionVM,
    })

  const check = usePermissions();
  const canWrite = check.canWrite("Templates");

  const getSampleData = () => {
    apiHelper(LeaseApplicationService.getApiLeaseApplicationSampleData, {
      showNotification: false,
    }).then((res) => { }).catch((err) => { })
  }

  useEffect(() => {
    if (id) { getTempleteData() }
    getSampleData()
  }, [id]);

  const handleSubmit = useCallback(async () => {
    try {
      setIsLoading(true);
      const apiCall = id
        ? apiHelper(TemplateService.putApiTemplate, {
          title:"Template Updated",
          successMessage: "Template edited successfully",
          failureMessage: "Unable to Add Template",
          showNotification: true,
        }, Number(id), postData)
        : apiHelper(TemplateService.postApiTemplate, {
          title:"Template Saved",
          successMessage: "Template saved successfully",
          failureMessage: "Unable to create Template",
          showNotification: true,
        }, postData)
      const res = await apiCall;
      if (res?.data?.success) {
        navigate("/organization/templates", { state: { activeTab: location.state?.fromTemplateTab || "LeaseApplication" } });
        //  setActiveSection("")
      }
    } catch (error) {
      console.error("Error occurred while submitting data:", error);
    } finally { setIsLoading(false); }
  }, [id, postData, navigate]);

  const getTempleteData = () => {
    apiHelper(TemplateService.getApiTemplate, { showNotification: false, }, Number(id))
      .then((res) => { if (res?.data?.success) { setPostData(res?.data?.success); } }).catch((err) => { });
  };

  const duplicateTemplete = () => {
    if (id) {
      apiHelper(TemplateService.postApiTemplateDuplicate, {
        title:"Template duplicated",
        successMessage: "Template duplicated successfully",
        failureMessage: "Unable to Duplicate Template",
        showNotification: true,
      }, Number(id)).then((res) => {
        if (res?.data?.success) { navigate(`/organization/templates/edit/${res?.data?.success.id}`); }
      }).catch((err) => { });
    }
  };

  const deleteTemplete = () => {
    if (id) {
      apiHelper(TemplateService.deleteApiTemplate, {
        title:"Template Deleted",
        successMessage: "Template deleted successfully",
        failureMessage: "Unable to Delete Template",
        showNotification: true,
      }, Number(id)).then((res) => {
        navigate("/organization/templates", { state: { activeTab: location.state?.fromTemplateTab || "LeaseApplication" } });
      }).catch((err) => { });
    }
  };

  const HandleEditFeature = (sectionName: string) => {
    if (sectionName) {
      setActiveSection(sectionName);
    } else { setActiveSection("") }
  };

  const handleSectionToggle = useCallback((sectionName: string) => {
    setPostData((prevData) => {
      const newData = { ...prevData };
      newData.leaseApplicationTemplateSections = newData.leaseApplicationTemplateSections || {};
      if (sectionName === "Signature") {
        if (
          newData.leaseApplicationTemplateSections[sectionName] &&
          newData.leaseApplicationTemplateSections[sectionName].clause !== ""
        ) {
          newData.leaseApplicationTemplateSections[sectionName].clause = "";
        } else {
          newData.leaseApplicationTemplateSections[sectionName] =
            getDefaultSectionDetails(sectionName);
        }
      }
      else {
        if (newData.leaseApplicationTemplateSections[sectionName]) {
          delete newData.leaseApplicationTemplateSections[sectionName];
        } else {
          newData.leaseApplicationTemplateSections[sectionName] = getDefaultSectionDetails(sectionName);
        }
      }

      return newData;
    });
  }, [setPostData]);

  const getDefaultSectionDetails = (sectionName: string) => {
    const updatedQuestions = [
      ...(postData?.leaseApplicationTemplateSections?.Questionnaires?.questions || []),
    ];
    switch (sectionName) {
      case "RentIncomeCheck":
      case "CreditCheck":
      case "CurrentAddress":
      case "EmploymentHistory":
      case "References":
        return { requiredRange: 2 };
      case "Questionnaires":
        return {
          questions: [
            {
              id: updatedQuestions?.length + 1,
              questionTypeId: 1,
              questionText: "Do you smoke?",
              choices: [
                { id: 1, optionText: "Yes" },
                { id: 2, optionText: "No" },
              ],
              followUpQuestionChoiceId: 1,
              followUpQuestionText: "Please provide details?",
            },
          ],
          requiredRange: 8,
        };
      case "ApplicationFees":
        return { clause: "Fee is non refundable", fee: 50 };
      case "Signature":
        return { clause: "Are You agree for everything" };
      default:
        return {};
    }
  };

  const getSectionData = () => {
    apiHelper(LeaseApplicationService.getApiLeaseApplicationLeaseApplicationSectionTypes, {
      showNotification: false,
    }).then((res) => {
      const updatedData = res?.data?.success?.map((section) => ({
        ...section,
        editable: ![1, 2, 3].includes(section.id),
        disbale:
          section.name.replace(/\s/g, "") === "Signature"
            ? !!postData?.leaseApplicationTemplateSections?.Signature?.clause
            : !!postData?.leaseApplicationTemplateSections?.[section.name.replace(/\s/g, "")],

        onClick: canWrite ? () => handleSectionToggle(section.name.replace(/\s/g, "")) : undefined,
        editClick:
          canWrite && (section.name.replace(/\s/g, "") !== "Signature" ||
            !!postData?.leaseApplicationTemplateSections?.Signature?.clause)
            ? () => HandleEditFeature(section.name.replace(/\s/g, ""))
            : undefined
      }));
      setData(updatedData);
    }).catch((err) => { })
  }
  useEffect(() => {
    getSectionData()
  }, [id, postData, handleSectionToggle]);

   

  return (
    <PermissionGuard permission="Templates" level={AccessLevelEnum.ReadOnly} fallback={<NoAccess />}>
      <div className="application-section">
        <QuestionHeader
          postData={postData}
          setPostData={setPostData}
          duplicateTemplete={duplicateTemplete}
          deleteTemplete={deleteTemplete}
          previewApplication={previewApplication}
        />
        <div className="application-section-main">
          <section className="application_review">
            <div className="application_review_left">
              <QuestionOptional
                data={data}
                postData={postData}
                activeSection={activeSection}
              />

            </div>
            
            <div className="application_review_right ps-3">
              {activeSection && (
              <div className="onboardBoxContainer" >
                 <SectionHeader title={activeSection} icon={false}  subTitle={activeSection === "EmploymentHistory" ? 
                  "Please Specify Years of Employment History that will be Required From the Tenant" :
                  activeSection === "References" ? "Please Specify No of Reference(s)  that will be Required From the Tenant"  :""} />
                <div className="sectionStep w-100 ">
                {activeSection === "EmploymentHistory" && (<EmploymentHistoryEdit postData={postData} setPostData={setPostData} />)}
              {activeSection === "References" && (<Reference postData={postData} setPostData={setPostData} />)}
              {activeSection === "Questionnaires" && (<QuestionHistoryEdit postData={postData} setPostData={setPostData} />)}
              {activeSection === "ApplicationFees" && (<FeeTemplete postData={postData} setPostData={setPostData} />)}
              {activeSection === "Signature" && (<ConfirmSignature postData={postData} setPostData={setPostData} />)}
              </div>
             <div className="application-section-bttom-view1 ">
          {!activeSection ?
            <AddTraspButton
              type="button"
              bname="Cancel"
              onClick={() => { navigate(-1) }}
              border={false}
              width="fit-content"
            /> :
            <AddTraspButton
              type="button"
              bname="Cancel"
              onClick={() => { setActiveSection("") }}
              border={false}
              width="fit-content"
            />
          }

          {canWrite && (
          <AddButtonWithArrow
            className={previewApplication ? "bg-secondary" : ""}
            buttonname={isLoading ? "Saving..." : "Save"}
            onClick={()=>setActiveSection("")}
            disabled={isLoading ? true : false}
            minWidth="100px"
          />
          )}
        </div>
              </div>
              )}
              {!activeSection && (
                <>                                        
                  <div className="application-preview-header">                     
                    <p className="heading black">Lease Application Preview <span> <CustomTooltip children={<img className="color-white-icon" alt="application info" src={GetImages.InfoIcon} />} content={"Not actual data: This Preview is for Visualization Purposes Only and Does Not Contain Real Data."} /></span></p>                  
                  </div>
                  <BoardingReview
                    postData={postData}
                    editable={false}
                    isEditClick={undefined}
                    formData={undefined}
                    setFormData={undefined}
                    steps={undefined}
                    isPreview={true}
                  />
                </>
              )}
            </div>
          </section>
        </div>
        {!activeSection && (
        <div className="application-section-bttom-view1 ">
          {!activeSection ?
            <AddTraspButton
              type="button"
              bname="Cancel"
              onClick={() => { navigate(-1) }}
              border={false}
              width="fit-content"
            /> :
            <AddTraspButton
              type="button"
              bname="Cancel"
              onClick={() => { setActiveSection("") }}
              border={false}
              width="fit-content"
            />
          }

          {canWrite && (
          <AddButtonWithArrow
            className={previewApplication ? "bg-secondary" : ""}
            buttonname={isLoading ? "Saving..." : "Save Template"}
            onClick={handleSubmit}
            disabled={isLoading ? true : false}
            minWidth="100px"
          />
          )}
        </div>
        )}
      </div>
    </PermissionGuard>
  );
};

export default AddTemplate;
